import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface Props {
  children: ReactNode;
  /**
   * Cosa mostrare al posto della pagina intera quando si guasta solo un
   * pezzo (per esempio un widget della dashboard). Senza, resta la schermata
   * d'errore di tutta l'app.
   */
  fallback?: (riprova: () => void) => ReactNode;
  /** Quando cambia, si riprova da capo (es. nuove impostazioni del widget). */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🔍 ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  public componentDidUpdate(precedenti: Props) {
    if (this.state.hasError && precedenti.resetKey !== this.props.resetKey) this.handleReset();
  }

  public render() {
    if (this.state.hasError && this.props.fallback) {
      return this.props.fallback(this.handleReset);
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <span>Errore dell'Applicazione</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Dettagli Errore:</h3>
                <p className="text-sm text-red-700 font-mono">
                  {this.state.error?.message || 'Errore sconosciuto'}
                </p>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="bg-gray-50 border rounded-lg p-4">
                  <summary className="font-semibold text-gray-700 cursor-pointer">
                    Stack Trace (Development)
                  </summary>
                  <pre className="text-xs text-gray-600 mt-2 overflow-auto">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="flex space-x-3">
                <Button onClick={this.handleReset} variant="outline">
                  Riprova
                </Button>
                <Button onClick={this.handleReload}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ricarica Pagina
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}